import { registerSW } from 'virtual:pwa-register';
import { hasUnsavedChanges } from '../guards/unsavedChanges';
import { isAutoReloadBlocked } from './reloadSafety';
import { shouldApplyOnNavigation, shouldApplyOnDetection } from './updatePolicy';

export { shouldApplyOnNavigation };

/*
  PWA service-worker update handling.

  vite.config uses registerType 'prompt' (manual), so a freshly deployed SW waits
  until we explicitly apply it. We never apply at detection time — a surprise hard
  reload mid-session is jarring and, worst case, can land on the OIDC `/callback`
  while the single-use auth code is being exchanged, breaking sign-in.

  Policy:
    - A new SW is detected → keep it waiting (no reload).
    - Apply (skip-waiting + reload) at the user's next real navigation, as long as
      the app is clean — see shouldApplyOnNavigation. updateSW(true) reloads the
      current document URL, which after the SPA soft-nav settles IS the
      destination route, so it feels like a normal page load.
    - If there are unsaved edits, a toast offers "Reload now"; otherwise the
      update just waits for the next clean navigation.
    - ONE exception, the public intake form: it is a single route that never
      navigates, so "the next navigation" never arrives and the update would wait
      forever. A clean intake page applies at detection instead — see
      shouldApplyOnDetection, and #721 for the stale-build symptom that caused it.

  Never caches API/financial data — see vite.config workbox notes.
*/

const UPDATE_POLL_MS = 60 * 60 * 1000; // hourly check for a new deployment

const listeners = new Set<() => void>();
let needRefresh = false;
let updateSW: ((reloadPage?: boolean) => Promise<void>) | undefined;

function emit() {
  for (const fn of listeners) fn();
}

function applyPending() {
  // updateSW(true) skip-waits the new SW and reloads the page.
  void updateSW?.(true);
}

/**
 * Wire up SW registration. Call once at startup.
 *
 * `currentPath` exists only as a test seam: the detection-time rule below needs the
 * path the client is actually on, and these suites run under the repo's default node
 * environment (vitest.config.ts) where there is no `window`. Switching this file to
 * jsdom instead is not an option — vite's import analysis then refuses to resolve
 * `virtual:pwa-register` before `vi.mock` can stand in for it.
 */
export function registerPwaUpdates(opts?: { currentPath?: () => string }): void {
  const currentPath = opts?.currentPath ?? (() => window.location.pathname);
  updateSW = registerSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => {
          void registration.update();
        }, UPDATE_POLL_MS);
      }
    },
    onNeedRefresh() {
      // Keep the new SW waiting; it's applied on the next navigation (or via the
      // toast's "Reload now"). Surface the pending state for the toast.
      needRefresh = true;
      emit();
      // ...except on the public intake form, where no navigation is ever coming and
      // an untouched form has nothing to lose by reloading now (#721). See
      // shouldApplyOnDetection for why the conservative gate doesn't apply there.
      if (
        shouldApplyOnDetection({
          isDirty: hasUnsavedChanges(),
          isReloadBlocked: isAutoReloadBlocked(),
          pathname: currentPath(),
        })
      ) {
        applyPending();
      }
    },
  });
}

/**
 * Called on every settled router navigation. Applies a pending update when the
 * navigation is a safe moment to reload (see shouldApplyOnNavigation).
 */
export function handleNavigation(nav: {
  historyAction: 'PUSH' | 'REPLACE' | 'POP';
  pathname: string;
}): void {
  if (
    shouldApplyOnNavigation({
      needRefresh,
      isDirty: hasUnsavedChanges(),
      historyAction: nav.historyAction,
      pathname: nav.pathname,
    })
  ) {
    applyPending();
  }
}

/** Is a service-worker update waiting to be applied? */
export function isUpdatePending(): boolean {
  return needRefresh;
}

/** Apply the pending update now (discards unsaved edits). Toast "Reload now". */
export function applyUpdateNow(): void {
  applyPending();
}

export function subscribePwaUpdate(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
